import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { Link } from '../../components/Link';
import { Modal } from '../../components/Modal';
import './meet-people-intro.css';

/** Where the intro was opened: by itself on the home, the ? beside the home's sign, or the ? on the Meet people page. */
export type IntroFrom = 'auto' | 'sign' | 'page';

/** The film (Motion MzozAxiLubQCCNjavkefyo, rendered once to the MP4 the web and both apps play). */
const FILM = '/media/meet-people-v1.mp4';
/** What its captions say, for a screen reader (the film itself is hidden from one). */
const SAID = 'You can always just talk to me. When you want people to talk to, I’ll do my best to help. '
  + 'I get to know everyone here, so I can find people you’d click with. Only if you want. What you tell me stays between us. '
  + 'I’ll introduce you, help you warm up, and look out for you the whole way. Ember. A place to be heard. A way to connect.';
/** The fold back into where it came from: how long, and how small it gets before it fades (a spark, not a card). */
const FOLD_MS = 760;
const FOLD_PX = 24;

/**
 * The Meet people intro (2026-10-27 founder): what Meet people is, told by a short film instead of words on the page.
 * The home opens it by itself once a person (GET /api/me/chats friends.intro); the ? beside the home's sign and the one
 * on the page open it any time after. From the home it leads on to the page; on the page it only closes.
 *
 * It ends by folding back into where it came from (2026-10-28 founder): when the film is over, or closed before then,
 * the sheet shrinks into a warm spark that lands on `foldTo` — the home's sign, or the page's ? — so the way back to it
 * is where it went. Following it on to the page leaves at once; with reduced motion it simply closes.
 */
export function MeetPeopleIntro({ from, onClose, foldTo }: { from: IntroFrom; onClose: () => void; foldTo?: () => Element | null }): ReactElement {
  const titleId = useId();
  const inner = useRef<HTMLDivElement>(null);
  const film = useRef<HTMLVideoElement>(null);
  const [still] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true);
  // Not playing yet: a still (reduced-motion) start, or a browser that refused to start it. The pill starts it.
  const [idle, setIdle] = useState(still);
  const play = (): void => {
    const v = film.current;
    if (!v) return;
    setIdle(false);
    v.play().catch(() => setIdle(true));
  };
  useEffect(() => { if (!still) play(); }, []);

  const folding = useRef(false);
  const fold = (): void => {
    if (folding.current) return;
    folding.current = true;
    const sheet = inner.current?.closest<HTMLElement>('.ember-modal');
    const backdrop = sheet?.parentElement;
    const to = foldTo?.()?.getBoundingClientRect();
    if (still || !sheet || !backdrop || !to || typeof sheet.animate !== 'function') { onClose(); return; }
    const at = sheet.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (at.left + at.width / 2);
    const dy = to.top + to.height / 2 - (at.top + at.height / 2);
    const s = FOLD_PX / Math.max(at.width, at.height);
    sheet.classList.add('is-folding');
    backdrop.animate(
      [{}, { backgroundColor: 'rgba(58, 51, 29, 0)', backdropFilter: 'blur(0px)', WebkitBackdropFilter: 'blur(0px)' }],
      { duration: FOLD_MS * 0.7, easing: 'ease-out', fill: 'forwards' },
    );
    const flight = sheet.animate(
      [
        { transform: 'none', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s})`, borderRadius: `${at.width}px`, opacity: 1, offset: 0.85 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s})`, borderRadius: `${at.width}px`, opacity: 0 },
      ],
      { duration: FOLD_MS, easing: 'cubic-bezier(.6, 0, .3, 1)', fill: 'forwards' },
    );
    flight.onfinish = () => onClose();
  };

  return (
    // Focus on the dialog, not its ×: opened by itself, the × wore a focus ring nobody had asked for (dev 2026-10-27).
    <Modal sheet="phone" onClose={fold} labelledBy={titleId} className="mp-intro" focusDialog>
      <div ref={inner} className="mp-intro-inner" data-log-seen="friends_intro" data-log-state={from}>
        <div className="mp-intro-head">
          <h2 id={titleId} className="mp-intro-title">
            {from === 'auto' ? <span className="mp-intro-new">new!</span> : null}
            Meet people
          </h2>
          <button type="button" className="mp-intro-x" aria-label="Close" data-log="friends_intro_close" data-log-state="dismiss" onClick={fold}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div className="mp-intro-film">
          <video ref={film} src={FILM} muted playsInline preload="auto" aria-hidden onEnded={fold} />
          <p className="mp-intro-said">{SAID}</p>
          {idle ? (
            <button type="button" className="mp-intro-play" data-log="friends_intro_replay" data-log-state="play" onClick={play}>
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden><path d="M8 5.5v13l10.5-6.5z" fill="currentColor" /></svg>
              Play
            </button>
          ) : null}
        </div>
        <div className="mp-intro-actions">
          {from === 'page' ? (
            <button type="button" className="ember-btn ember-btn-primary" data-log="friends_intro_close" data-log-state="got_it" onClick={fold}>Got it</button>
          ) : (
            <>
              <Link to="/friends" className="ember-btn ember-btn-primary" data-log="friends_intro_go" data-log-state={from} onClick={onClose}>Take a look</Link>
              <button type="button" className="ember-btn ember-btn-ghost" data-log="friends_intro_close" data-log-state="not_now" onClick={fold}>Not now</button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
